// A blocked account (Admin → Users → Block, or the automatic rules). Once the API says so, at
// sign-in with the right password or on any request of a signed-in member (lib/account-block.ts),
// this screen takes the place of the whole site: why, in the words staff chose, and writing to
// support. A member still signed in can log out; one who just tried to sign in goes back.
import type { ReactNode } from 'react';
import { clearAccountBlocked, useAccountBlock, type AccountBlock } from '../lib/account-block';
import { T, t } from '../i18n';
import { GateScreen, SUPPORT_EMAIL, useLeave } from './GateScreen';

export function AccountGate({ children }: { children: ReactNode }) {
  const block = useAccountBlock();
  if (!block) return <>{children}</>;
  return <BlockedAccountScreen block={block} />;
}

function BlockedAccountScreen({ block }: { block: AccountBlock }) {
  const leave = useLeave();
  // i18n: subject line of the email to support from the blocked-account screen
  const subject = t('My account is blocked');
  return (
    <GateScreen
      id="account-blocked-title"
      // i18n: the browser tab's title when the member's account is blocked
      docTitle={t('Account blocked')}
      title={t('Your account is blocked')}
      actions={
        <>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`}
            className="btn-primary pressable min-h-[52px] w-full text-[16px]"
          >
            {t('Email support')}
          </a>
          {block.signedIn ? (
            <button
              type="button"
              onClick={() => void leave()}
              className="btn-ghost pressable mt-3 min-h-[52px] w-full text-[16px]"
            >
              {t('Log out')}
            </button>
          ) : (
            <button
              type="button"
              onClick={clearAccountBlocked}
              className="btn-ghost pressable mt-3 min-h-[52px] w-full text-[16px]"
            >
              {t('Back')}
            </button>
          )}
        </>
      }
    >
      <p className="mt-3 text-pretty text-[16px] leading-relaxed text-graphite">
        {t('You can’t earn or cash out with this account while it’s blocked.')}
      </p>
      {(block.reason || block.note) && (
        <div className="card mt-6 p-4">
          {block.reason && (
            <>
              <p className="eyebrow">
                {
                  // i18n: label above the reason an account is blocked
                  t('Why')
                }
              </p>
              <p className="mt-1.5 text-pretty text-[16px] leading-snug text-ink">{block.reason}</p>
            </>
          )}
          {block.note && (
            <div className={block.reason ? 'mt-4 border-t border-hairline pt-3' : ''}>
              <p className="eyebrow">
                {
                  // i18n: label above a message staff wrote to the member whose account is blocked
                  t('Message from our team')
                }
              </p>
              <p
                dir="auto"
                className="mt-1.5 whitespace-pre-line text-pretty text-[15px] leading-relaxed text-graphite [overflow-wrap:anywhere]"
              >
                {block.note}
              </p>
            </div>
          )}
        </div>
      )}
      <p className="mt-5 text-pretty text-[15px] leading-snug text-graphite">
        <T
          msg="<b>Think this is a mistake?</b> Write to {email} from your account’s email address and we’ll look into it."
          values={{ email: SUPPORT_EMAIL }}
          tags={{ b: (c) => <span className="font-semibold text-ink">{c}</span> }}
        />
      </p>
    </GateScreen>
  );
}
